import React from 'react';
import { Statistic } from '../components/StockTable';
import { TickerDisplayData } from '../types/ticker';
import { UpArrowIcon, DownArrowIcon } from '../components/Icons';

// Daily statistics configuration
export const DAILY_STATISTICS: Statistic[] = [
  { key: 'preMarket', label: 'Pre-Market' },
  { key: 'preMarketChange', label: 'Change (PM)' },
  { key: 'preMarketChangePercent', label: 'Change % (PM)' },
  { key: 'afterHours', label: 'After-Hours' },
  { key: 'afterHoursChange', label: 'Change (AH)' },
  { key: 'afterHoursChangePercent', label: 'Change % (AH)' },
  { key: 'latest', label: 'Latest' }, // This will be dynamically updated
  { key: 'change', label: 'Change' },
  { key: 'changePercent', label: 'Change %' },
  { key: 'open', label: 'Open' },
  { key: 'high', label: 'Day High' },
  { key: 'low', label: 'Day Low' },
  { key: 'volume', label: 'Volume' },
];

// Formatting helper functions
export const formatPrice = (price: number) => `$${price.toFixed(2)}`;
export const formatChange = (change: number) => `${change >= 0 ? '+' : ''}${change.toFixed(2)}`;
export const formatChangePercent = (changePercent: number) => `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%`;
export const formatVolume = (volume: number) => volume.toLocaleString();

// Get the timestamp when market status changed to 'open' from localStorage (fallback)
const getMarketOpenTimestampFromStorage = (): number | null => {
  const stored = localStorage.getItem('stocktable-market-open-timestamp');
  return stored ? parseInt(stored, 10) : null;
};

// Set the timestamp when market status changes to 'open' (for localStorage fallback)
export const setMarketOpenTimestamp = (timestamp: number): void => {
  localStorage.setItem('stocktable-market-open-timestamp', timestamp.toString());
};

// Check if market changed to open within the past 15 minutes
// Also checks if market just closed within the past 15 minutes (should still show "Latest"/"Open")
// Uses server-provided timestamps if available, otherwise falls back to localStorage
export const isMarketOpenWithin15Minutes = (
  marketOpenTimestamp: string | null | undefined,
  marketCloseTimestamp: string | null | undefined
): boolean => {
  const now = Date.now();
  
  // If market just closed within the past 15 minutes, we should still show "Latest"/"Open"
  // because prices are still from when market was open (with 15-minute delay)
  if (marketCloseTimestamp) {
    const closeTimestamp = new Date(marketCloseTimestamp).getTime();
    if (!isNaN(closeTimestamp)) {
      const timeSinceCloseMs = now - closeTimestamp;
      const timeSinceCloseMinutes = timeSinceCloseMs / (1000 * 60);
      if (timeSinceCloseMinutes <= 15) {
        return true; // Still within 15 minutes of close, show "Latest"/"Open"
      }
    }
  }
  
  // Check if market opened within the past 15 minutes
  let openTimestamp: number | null = null;
  
  // Prefer server-provided timestamp
  if (marketOpenTimestamp) {
    const serverTimestamp = new Date(marketOpenTimestamp).getTime();
    if (!isNaN(serverTimestamp)) {
      openTimestamp = serverTimestamp;
    }
  }
  
  // Fallback to localStorage if server timestamp not available
  if (!openTimestamp) {
    openTimestamp = getMarketOpenTimestampFromStorage();
  }
  
  if (!openTimestamp) {
    return false;
  }
  
  const timeDifferenceMs = now - openTimestamp;
  const timeDifferenceMinutes = timeDifferenceMs / (1000 * 60);
  
  return timeDifferenceMinutes <= 15;
};

// Get latest label based on market status and 15-minute delay logic
export const getLatestLabel = (
  marketStatus: 'open' | 'pre-market' | 'after-hours' | 'closed' | null,
  marketOpenTimestamp?: string | null,
  marketCloseTimestamp?: string | null
): string => {
  // If market is open, check if it changed to open within the past 15 minutes
  if (marketStatus === 'open') {
    return isMarketOpenWithin15Minutes(marketOpenTimestamp, marketCloseTimestamp) ? 'Last Close' : 'Latest';
  }
  
  // If market just closed/after-hours within the past 15 minutes, show "Latest" (prices still from when market was open)
  if (marketStatus === 'closed' || marketStatus === 'after-hours') {
    return isMarketOpenWithin15Minutes(marketOpenTimestamp, marketCloseTimestamp) ? 'Latest' : 'Last Close';
  }
  
  // For pre-market, show "Last Close"
  return 'Last Close';
};

// Get open label based on market status and 15-minute delay logic
export const getOpenLabel = (
  marketStatus: 'open' | 'pre-market' | 'after-hours' | 'closed' | null,
  marketOpenTimestamp?: string | null,
  marketCloseTimestamp?: string | null
): string => {
  // If market is open, check if it changed to open within the past 15 minutes
  if (marketStatus === 'open') {
    return isMarketOpenWithin15Minutes(marketOpenTimestamp, marketCloseTimestamp) ? 'Last Open' : 'Open';
  }
  
  // If market just closed/after-hours within the past 15 minutes, show "Open" (prices still from when market was open)
  // After 15 minutes, show "Last Open" for consistency with "Last Close" label
  if (marketStatus === 'closed' || marketStatus === 'after-hours') {
    return isMarketOpenWithin15Minutes(marketOpenTimestamp, marketCloseTimestamp) ? 'Open' : 'Last Open';
  }
  
  // For pre-market, show "Last Open" (prices are from previous day's open since market hasn't opened yet)
  if (marketStatus === 'pre-market') {
    return 'Last Open';
  }
  
  // Default fallback
  return 'Open';
};

// Get cached label from localStorage or default
export const getCachedLabel = (): string => {
  const cached = localStorage.getItem('stocktable-latest-label');
  return cached || 'Latest';
};

// Volume arrow info type
export interface VolumeArrowInfo {
  direction: 'up' | 'down' | null;
  isGood: boolean;
  isNeutral: boolean;
}

// Get volume arrow direction and color based on price and volume changes
export const getVolumeArrowInfo = (
  ticker: TickerDisplayData,
  marketStatus: 'open' | 'pre-market' | 'after-hours' | 'closed' | null
): VolumeArrowInfo => {
  const hasPrevVolume = ticker.prevVolume && ticker.prevVolume > 0;
  
  // Only show arrows if market is not open and we have previous volume data from batch API fallback
  if (marketStatus === 'open' || !hasPrevVolume || ticker.prevVolume === undefined) {
    return { direction: null, isGood: false, isNeutral: false };
  }
  
  const volumeIncrease = ticker.volume > ticker.prevVolume;
  const priceIncrease = ticker.latest >= ticker.prevClose;
  
  // Always show arrows when we have data
  if (volumeIncrease) {
    // Volume is higher
    if (priceIncrease) {
      // Price higher + Volume higher = Green up arrow
      return { direction: 'up', isGood: true, isNeutral: false };
    } else {
      // Price lower + Volume higher = Red up arrow
      return { direction: 'up', isGood: false, isNeutral: false };
    }
  } else {
    // Volume is lower
    if (priceIncrease) {
      // Price higher + Volume lower = Neutral down arrow
      return { direction: 'down', isGood: false, isNeutral: true };
    } else {
      // Price lower + Volume lower = Neutral down arrow
      return { direction: 'down', isGood: false, isNeutral: true };
    }
  }
};

// Get volume tooltip text
export const getVolumeTooltip = (
  ticker: TickerDisplayData,
  marketStatus: 'open' | 'pre-market' | 'after-hours' | 'closed' | null
): string => {
  const hasPrevVolume = ticker.prevVolume && ticker.prevVolume > 0;
  
  // Only show tooltip if market is not open and we have previous volume data
  if (marketStatus === 'open' || !hasPrevVolume || ticker.prevVolume === undefined) {
    return '';
  }
  
  return `Compared to ${ticker.prevVolume.toLocaleString()} the previous day`;
};

// Format volume with arrow icon based on price and volume changes
export const formatVolumeWithArrow = (
  ticker: TickerDisplayData,
  marketStatus: 'open' | 'pre-market' | 'after-hours' | 'closed' | null
): React.ReactNode => {
  const volumeText = formatVolume(ticker.volume);
  const arrowInfo = getVolumeArrowInfo(ticker, marketStatus);
  const tooltipText = getVolumeTooltip(ticker, marketStatus);
  
  return (
    <span className="inline-flex items-center gap-1">
      {volumeText}
      {arrowInfo.direction === 'up' && (
        <div className="relative group/arrow">
          <UpArrowIcon 
            className={`w-3 h-3 ${
              arrowInfo.isNeutral 
                ? 'text-text-light-neutral dark:text-text-secondary' 
                : arrowInfo.isGood 
                  ? 'text-green-light dark:text-green-500' 
                  : 'text-red-light dark:text-red-500'
            }`} 
          />
          {tooltipText && (
            <div className="absolute hidden group-hover/arrow:block left-0 bottom-full mb-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[99999] font-sans">
              {tooltipText}
            </div>
          )}
        </div>
      )}
      {arrowInfo.direction === 'down' && (
        <div className="relative group/arrow">
          <DownArrowIcon 
            className={`w-3 h-3 ${
              arrowInfo.isNeutral 
                ? 'text-text-light-neutral dark:text-text-secondary' 
                : arrowInfo.isGood 
                  ? 'text-green-light dark:text-green-500' 
                  : 'text-red-light dark:text-red-500'
            }`} 
          />
          {tooltipText && (
            <div className="absolute hidden group-hover/arrow:block left-0 bottom-full mb-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[99999] font-sans">
              {tooltipText}
            </div>
          )}
        </div>
      )}
    </span>
  );
};

